export type Stage = {
  id: string;
  name: string;
  color: string;
  description: string;
  aiOn: boolean;
  requireAttribute: boolean;
  attributeName: string;
};

export type ScriptSection = {
  id: string;
  name: string;
  items: string[];
};

export type EventType = {
  id: string;
  name: string;
  minutes: number;
};

export type Reminder = {
  dueAt: number;
  time: string;
  note: string;
};

export type LastCall = {
  at: string;
  direction: string;
  durationSeconds: number | null;
};

export type LeadState = {
  stageId: string;
  aiOn: boolean;
  checks: string[];
  reminder: Reminder | null;
  attributes: Record<string, string>;
  lastCall: LastCall | null;
  whatsappNumber: string;
  instagramId: string;
  phoneId: string;
  notes: string;
  sameAs: string;
  tagIds: string[];
};

export type Tag = {
  id: string;
  name: string;
  color: string;
};

export type LeadField = {
  id: string;
  name: string;
};

export type ColumnLayout = {
  id: string;
  width: number;
  visible: boolean;
};

export type BusinessSetup = {
  stages: Stage[];
  tags: Tag[];
  fields: LeadField[];
  columns: ColumnLayout[];
  scripts: ScriptSection[];
  eventTypes: EventType[];
  leads: Record<string, LeadState>;
};

const STORAGE_KEY = "fd24.businessSetup.v1";

export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function businessId(): string {
  return (
    sessionStorage.getItem("wabaId") ||
    sessionStorage.getItem("display_phone_number") ||
    localStorage.getItem("email") ||
    "business"
  );
}

function starter(): BusinessSetup {
  return {
    stages: [],
    scripts: [],
    eventTypes: [],
    tags: [],
    fields: [],
    columns: [],
    leads: {},
  };
}

function readAll(): Record<string, BusinessSetup> {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function withoutOldSeed(stages: Stage[]): Stage[] {
  const seed = [
    { id: "new", name: "new" },
    { id: "talking", name: "in conversation" },
    { id: "booked", name: "booked" },
    { id: "won", name: "won" },
  ];
  if (stages.length !== seed.length) return stages;
  const untouched = stages.every((stage, index) => (
    stage.id === seed[index].id
    && stage.name.trim().toLowerCase() === seed[index].name
    && !stage.description.trim()
  ));
  return untouched ? [] : stages;
}

export function loadSetup(id: string = businessId()): BusinessSetup {
  const saved = readAll()[id];
  if (!saved || !Array.isArray(saved.stages)) return starter();
  return {
    stages: withoutOldSeed(saved.stages.map(normalizeStage).filter((stage) => stage.name)),
    scripts: Array.isArray(saved.scripts) ? saved.scripts : [],
    eventTypes: Array.isArray(saved.eventTypes) ? saved.eventTypes : [],
    tags: Array.isArray(saved.tags) ? saved.tags.map(normalizeTag).filter((tag) => tag.name) : [],
    fields: Array.isArray(saved.fields) ? saved.fields.map(normalizeField).filter((field) => field.name) : [],
    columns: Array.isArray(saved.columns) ? saved.columns.filter((column) => column && column.id) : [],
    leads: saved.leads && typeof saved.leads === "object" ? saved.leads : {},
  };
}

export function saveSetup(setup: BusinessSetup, id: string = businessId()) {
  const all = readAll();
  all[id] = setup;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

export function emptyLead(): LeadState {
  return {
    stageId: "",
    aiOn: true,
    checks: [],
    reminder: null,
    attributes: {},
    lastCall: null,
    whatsappNumber: "",
    instagramId: "",
    phoneId: "",
    notes: "",
    sameAs: "",
    tagIds: [],
  };
}

export function phoneDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/** A mobile number is 10 digits. A leading 91 is removed so it does not have to be typed. */
export function tenDigitMobile(raw: string): { digits: string; error: string } {
  let digits = phoneDigits(raw);
  if (digits.startsWith("91") && digits.length === 12) digits = digits.slice(2);
  if (!digits) return { digits: "", error: "" };
  if (digits.length !== 10) return { digits: "", error: "Enter 10 digits. Do not add 91." };
  return { digits, error: "" };
}

export function mobileDisplay(raw: string): string {
  const digits = phoneDigits(raw);
  if (digits.startsWith("91") && digits.length === 12) return digits.slice(2);
  return digits;
}

export function numberInText(text: string): string {
  const matches = text.match(/\+?\d[\d\s().-]{6,}\d/g) || [];
  const found = matches.map(phoneDigits).find((digits) => digits.length >= 8 && digits.length <= 15);
  return found || "";
}

export function primaryKey(setup: BusinessSetup, key: string): string {
  const seen = new Set<string>();
  let current = key;
  while (current && !seen.has(current)) {
    const next = setup.leads[current]?.sameAs || "";
    if (!next || next === current) return current;
    seen.add(current);
    current = next;
  }
  return key;
}

export function leadState(setup: BusinessSetup, key: string): LeadState {
  const saved = setup.leads[primaryKey(setup, key)];
  if (!saved) return emptyLead();
  return {
    ...emptyLead(),
    ...saved,
    attributes: saved.attributes || {},
    tagIds: Array.isArray(saved.tagIds) ? saved.tagIds : [],
    lastCall: saved.lastCall || null,
    sameAs: "",
  };
}

export function rememberCalls(setup: BusinessSetup, calls: Array<{ key: string; lastCall: LastCall }>): BusinessSetup {
  if (calls.length === 0) return setup;
  const leads = { ...setup.leads };
  let changed = false;
  calls.forEach(({ key, lastCall }) => {
    const id = primaryKey({ ...setup, leads }, key);
    const current = leadState({ ...setup, leads }, id);
    const previous = current.lastCall?.at ? new Date(current.lastCall.at).getTime() : 0;
    const nextAt = new Date(lastCall.at).getTime();
    if (previous && nextAt && previous >= nextAt) return;
    leads[id] = { ...current, lastCall };
    changed = true;
  });
  return changed ? { ...setup, leads } : setup;
}

export function startStageId(stages: Stage[]): string {
  const named = stages.find((stage) => /convers/i.test(stage.name));
  return (named || stages[0])?.id || "";
}

export function attachChatsToPipeline(setup: BusinessSetup, keys: string[]): BusinessSetup {
  const entry = startStageId(setup.stages);
  if (!entry) return setup;
  const known = new Set(setup.stages.map((stage) => stage.id));
  const leads = { ...setup.leads };
  let changed = false;
  keys.forEach((key) => {
    const id = primaryKey({ ...setup, leads }, key);
    const current = leadState({ ...setup, leads }, id);
    if (current.stageId && known.has(current.stageId)) return;
    leads[id] = { ...current, stageId: entry };
    changed = true;
  });
  return changed ? { ...setup, leads } : setup;
}

function sameMobile(left: string, right: string): boolean {
  const a = tenDigitMobile(left).digits || phoneDigits(left);
  const b = tenDigitMobile(right).digits || phoneDigits(right);
  return Boolean(a) && a === b;
}

function findByPhone(setup: BusinessSetup, digits: string, exceptId: string): string {
  if (digits.length < 8) return "";
  for (const key of Object.keys(setup.leads)) {
    const id = primaryKey(setup, key);
    if (id === exceptId) continue;
    const person = leadState(setup, id);
    if (sameMobile(person.whatsappNumber, digits) || sameMobile(person.phoneId, digits)) return id;
    if (key.startsWith("wa:") && sameMobile(key.slice(3), digits)) return id;
  }
  return "";
}

function findByInstagram(setup: BusinessSetup, handle: string, exceptId: string): string {
  const needle = handle.trim().replace(/^@/, "").toLowerCase();
  if (!needle) return "";
  for (const key of Object.keys(setup.leads)) {
    const id = primaryKey(setup, key);
    if (id === exceptId) continue;
    const person = leadState(setup, id);
    if (person.instagramId.trim().replace(/^@/, "").toLowerCase() === needle) return id;
    if (key.startsWith("ig:") && key.slice(3).toLowerCase() === needle) return id;
  }
  return "";
}

function linkTo(setup: BusinessSetup, fromKey: string, toKey: string, extra: Partial<LeadState>): BusinessSetup {
  const from = primaryKey(setup, fromKey);
  const to = primaryKey(setup, toKey);
  if (!to) return setup;
  if (from === to) {
    const current = leadState(setup, to);
    const filled = { ...current };
    if (!filled.whatsappNumber && extra.whatsappNumber) filled.whatsappNumber = extra.whatsappNumber;
    if (!filled.instagramId && extra.instagramId) filled.instagramId = extra.instagramId;
    if (!filled.phoneId && extra.phoneId) filled.phoneId = extra.phoneId;
    return { ...setup, leads: { ...setup.leads, [to]: filled } };
  }
  const entry = startStageId(setup.stages);
  const older = leadState(setup, from);
  const newer = leadState(setup, to);
  const stageId = older.stageId && older.stageId !== entry ? older.stageId : (newer.stageId || older.stageId);
  const merged: LeadState = {
    ...newer,
    stageId,
    whatsappNumber: newer.whatsappNumber || older.whatsappNumber || extra.whatsappNumber || "",
    instagramId: newer.instagramId || older.instagramId || extra.instagramId || "",
    phoneId: newer.phoneId || older.phoneId || extra.phoneId || "",
    lastCall: newer.lastCall || older.lastCall,
    checks: Array.from(new Set(newer.checks.concat(older.checks))),
    attributes: { ...older.attributes, ...newer.attributes },
    reminder: newer.reminder || older.reminder,
    notes: newer.notes || older.notes,
    aiOn: newer.aiOn,
    sameAs: "",
  };
  const leads = { ...setup.leads };
  Object.keys(leads).forEach((key) => {
    if (leads[key].sameAs === from) leads[key] = { ...leads[key], sameAs: to };
  });
  leads[to] = merged;
  leads[from] = { ...emptyLead(), sameAs: to };
  return { ...setup, leads };
}

export function saveChannelId(
  setup: BusinessSetup,
  key: string,
  field: "whatsappNumber" | "instagramId" | "phoneId",
  raw: string
): BusinessSetup {
  const mobile = field === "instagramId" ? null : tenDigitMobile(raw);
  if (mobile?.error) return setup;
  const value = mobile ? mobile.digits : raw.trim().replace(/^@/, "");
  const id = primaryKey(setup, key);
  if (field === "instagramId" && value) {
    const match = findByInstagram(setup, value, id);
    if (match) return linkTo(setup, id, match, { instagramId: value });
  }
  if (field !== "instagramId" && value) {
    const match = findByPhone(setup, phoneDigits(value), id);
    if (match) return linkTo(setup, id, match, { [field]: value });
  }
  const current = leadState(setup, id);
  return { ...setup, leads: { ...setup.leads, [id]: { ...current, [field]: value } } };
}

function formFilledField(setup: BusinessSetup): LeadField | undefined {
  return setup.fields.find((field) => {
    const key = field.name.toLowerCase().replace(/[^a-z]/g, "");
    return key === "isformfilled" || key === "formfilled";
  });
}

/** Yes when this person filled the form. The column value wins when it is already yes or no. */
export function formFilledValue(setup: BusinessSetup, key: string, fromLead?: string): "yes" | "no" {
  const field = formFilledField(setup);
  const saved = field ? (leadState(setup, key).attributes[field.id] || "").trim().toLowerCase() : "";
  if (saved === "yes" || saved === "no") return saved;
  return fromLead === "yes" ? "yes" : "no";
}

/** Writes yes or no into the isFormFilled column from the lead record. */
export function applyFormFilled(
  setup: BusinessSetup,
  rows: Array<{ key: string; formFilled?: string }>
): BusinessSetup {
  const field = formFilledField(setup);
  if (!field) return setup;
  let changed = false;
  const leads = { ...setup.leads };
  rows.forEach((row) => {
    const id = primaryKey(setup, row.key);
    const value = row.formFilled === "yes" ? "yes" : "no";
    const current = leadState(setup, id);
    if ((current.attributes[field.id] || "") === value) return;
    changed = true;
    leads[id] = { ...current, attributes: { ...current.attributes, [field.id]: value } };
  });
  if (!changed) return setup;
  return { ...setup, leads };
}

/** Puts the stage and tags saved on the lead row onto the person the list is showing. */
export function applySavedLeadState(
  setup: BusinessSetup,
  rows: Array<{ key: string; stageId?: string; tagIds?: string[] }>
): BusinessSetup {
  const stages = new Set(setup.stages.map((stage) => stage.id));
  const tags = new Set(setup.tags.map((tag) => tag.id));
  let changed = false;
  const leads = { ...setup.leads };
  rows.forEach((row) => {
    const stageId = row.stageId && stages.has(row.stageId) ? row.stageId : "";
    const tagIds = (row.tagIds || []).filter((id) => tags.has(id));
    if (!stageId && tagIds.length === 0) return;
    const id = primaryKey({ ...setup, leads }, row.key);
    const current = leadState({ ...setup, leads }, id);
    const nextTags = tagIds.length > 0 ? tagIds : current.tagIds;
    const nextStage = stageId || current.stageId;
    if (nextStage === current.stageId && nextTags.join() === current.tagIds.join()) return;
    changed = true;
    leads[id] = { ...current, stageId: nextStage, tagIds: nextTags };
  });
  return changed ? { ...setup, leads } : setup;
}

export function foldChats(
  setup: BusinessSetup,
  rows: Array<{ key: string; channel: string; handle: string; customerNumber: string; preview?: string; instagramId?: string }>
): BusinessSetup {
  const next = rows.reduce((current, row) => {
    const number = phoneDigits(row.customerNumber || (row.channel === "whatsapp" ? row.key.replace(/^wa:/, "") : ""));
    const instagram = (row.instagramId || (row.channel === "instagram" ? row.handle || row.key.replace(/^ig:/, "") : "")).replace(/^@/, "");
    const spotted = number || numberInText(row.preview || "");
    if (spotted) {
      const match = findByPhone(current, spotted, primaryKey(current, row.key));
      if (match) return linkTo(current, row.key, match, { whatsappNumber: spotted, instagramId: instagram });
    }
    if (instagram) {
      const match = findByInstagram(current, instagram, primaryKey(current, row.key));
      if (match) return linkTo(current, row.key, match, { instagramId: instagram });
    }
    const id = primaryKey(current, row.key);
    const person = leadState(current, id);
    const filled = {
      ...person,
      whatsappNumber: person.whatsappNumber || spotted,
      instagramId: person.instagramId || instagram,
    };
    if (
      filled.whatsappNumber === person.whatsappNumber
      && filled.instagramId === person.instagramId
      && current.leads[id]
    ) {
      return current;
    }
    return { ...current, leads: { ...current.leads, [id]: filled } };
  }, setup);
  return attachChatsToPipeline(next, rows.map((row) => row.key));
}

export function normalizeField(field: LeadField): LeadField {
  return {
    id: field.id,
    name: String(field.name || "").trim(),
  };
}

const BUILT_IN_COLUMNS = [
  { id: "lead", label: "Lead", width: 180 },
  { id: "stage", label: "Stage", width: 120 },
  { id: "tags", label: "Tags", width: 150 },
  { id: "instagram", label: "Instagram", width: 150 },
  { id: "whatsapp", label: "WhatsApp number", width: 170 },
  { id: "phone", label: "Phone", width: 150 },
];

export type LeadColumn = { id: string; label: string; width: number; visible: boolean };

/** Columns for the leads table. A new field is included. Saved width and visibility stay. */
export function leadColumns(setup: BusinessSetup): LeadColumn[] {
  const saved = new Map((setup.columns || []).map((column) => [column.id, column]));
  const built = BUILT_IN_COLUMNS.map((column) => {
    const found = saved.get(column.id);
    return {
      id: column.id,
      label: column.label,
      width: found && found.width >= 80 ? found.width : column.width,
      visible: column.id === "lead" ? true : found ? found.visible !== false : true,
    };
  });
  const extra = setup.fields.map((field) => {
    const found = saved.get(field.id);
    return {
      id: field.id,
      label: field.name,
      width: found && found.width >= 80 ? found.width : 160,
      visible: found ? found.visible !== false : true,
    };
  });
  return built.concat(extra);
}

export function normalizeTag(tag: Tag): Tag {
  return {
    id: tag.id,
    name: String(tag.name || "").trim(),
    color: tag.color || "#2563eb",
  };
}

export function normalizeStage(stage: Stage): Stage {
  return {
    id: stage.id,
    name: stage.name,
    color: stage.color || "#3b82f6",
    description: stage.description || "",
    aiOn: stage.aiOn !== false,
    requireAttribute: Boolean(stage.requireAttribute),
    attributeName: stage.attributeName || "",
  };
}
